import React, { useCallback, useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "../styles/dashboard.css";
import "../styles/modern-category-grid.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import AccountSection from "../components/dashboard/account-section";
import { HashLoader } from "react-spinners";
import { getAccountsUnder1m, getAccountsUnder5h, getProductsByType } from "../api/product.api";
import ToastNotification, {
  showToast,
  showTopCenterToast,
} from "../components/toasts/ToastNotification";
import TopUpSection from "../components/dashboard/topup-section";
import UtilitySection from "../components/dashboard/utility-section";
import { getPinnedProducts } from "../api/recommend.api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { MdElectricBolt } from "react-icons/md";
import { IoIosStar } from "react-icons/io";
import ScrollToTopButton from "../components/scroll-to-top/scroll-to-top-button";
import GiftCardSection from "../components/dashboard/giftcard-section";
import { ReactComponent as MainBanner01 } from "../assets/images/banners/main-banner-01.svg";
import { ReactComponent as MainBanner02 } from "../assets/images/banners/main-banner-02.svg";
import { ReactComponent as MainBanner03 } from "../assets/images/banners/main-banner-03.svg";
import { ReactComponent as MainBanner04 } from "../assets/images/banners/main-banner-04.svg";
import { ReactComponent as MainBanner05 } from "../assets/images/banners/main-banner-05.svg";

const banners = [
  <MainBanner01 className="banner-image" />,
  <MainBanner02 className="banner-image" />,
  <MainBanner03 className="banner-image" />,
  <MainBanner04 className="banner-image" />,
  <MainBanner05 className="banner-image" />,
];

const categories = [
  {
    name: "Tài Khoản Game",
    image: require("../assets/images/icons/game-icon.png"),
    link: "/category/game-account"
  },
  {
    name: "Nạp Game Mobile",
    image: require("../assets/images/icons/topup-icon.png"),
    link: "/category/top-up"
  },
  {
    name: "Tiện Ích",
    image: require("../assets/images/icons/util-icon.png"),
    link: "/category/utility-account"
  },
  {
    name: "Gift Card",
    image: require("../assets/images/icons/gift-icon.png"),
    link: "/category/gift-card"
  },
];

const ACCOUNT_PAGE_SIZE = 12;

const Dashboard = () => {
  const [accounts, setAccounts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsLoadingMore, setAccountsLoadingMore] = useState(false);
  const [accountPage, setAccountPage] = useState(1);
  const [hasMoreAccounts, setHasMoreAccounts] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("latest");
  const accountRequestRef = useRef(0);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Iampesmobile.com";
    fetchProductData();
  }, []);

  useEffect(() => {
    if (redirect) {
      showTopCenterToast("Đăng nhập thành công", "success");
    }
  }, [redirect]);

  const mergeUniqueById = (oldItems = [], newItems = []) => {
    const seen = new Set();
    return [...oldItems, ...newItems].filter((item) => {
      const id = item?._id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const fetchAccountsByCategory = useCallback(
    async (category, page = 1, reset = false) => {
      const requestId = ++accountRequestRef.current;
      if (reset) {
        setAccountsLoading(true);
        setHasMoreAccounts(true);
      } else {
        setAccountsLoadingMore(true);
      }

      try {
        let accountRes = null;
        let nextItems = [];
        let canLoadMore = false;

        if (category === "latest") {
          accountRes = await getProductsByType("game_account", ACCOUNT_PAGE_SIZE, page);
          if (accountRes?.success) {
            nextItems = accountRes.data.products || [];
            const totalPages = Number(accountRes.data?.totalPages || 1);
            canLoadMore = page < totalPages;
          }
        } else if (category === "pinned") {
          accountRes = await getPinnedProducts(page * ACCOUNT_PAGE_SIZE);
          if (accountRes?.success) {
            nextItems = accountRes.data.pinnedProducts || [];
            canLoadMore = nextItems.length >= page * ACCOUNT_PAGE_SIZE;
          }
        } else if (category === "under_1m") {
          accountRes = await getAccountsUnder1m(page * ACCOUNT_PAGE_SIZE);
          if (accountRes?.success) {
            nextItems = accountRes.data.accounts || [];
            canLoadMore = nextItems.length >= page * ACCOUNT_PAGE_SIZE;
          }
        } else if (category === "under_5h") {
          accountRes = await getAccountsUnder5h(page * ACCOUNT_PAGE_SIZE);
          if (accountRes?.success) {
            nextItems = accountRes.data.accounts || [];
            canLoadMore = nextItems.length >= page * ACCOUNT_PAGE_SIZE;
          }
        }

        if (requestId !== accountRequestRef.current) return;

        if (reset) {
          setAccounts(nextItems);
        } else {
          setAccounts((prev) => mergeUniqueById(prev, nextItems));
        }

        setAccountPage(page);
        setHasMoreAccounts(canLoadMore);
      } catch (error) {
        showToast(error.message, "error");
      } finally {
        if (requestId !== accountRequestRef.current) return;
        if (reset) {
          setAccountsLoading(false);
        } else {
          setAccountsLoadingMore(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    setAccounts([]);
    setAccountPage(1);
    setHasMoreAccounts(true);
    fetchAccountsByCategory(selectedCategory, 1, true);
  }, [selectedCategory, fetchAccountsByCategory]);

  const handleLoadMoreAccounts = useCallback(() => {
    if (accountsLoading || accountsLoadingMore || !hasMoreAccounts) return;
    fetchAccountsByCategory(selectedCategory, accountPage + 1, false);
  }, [accountsLoading, accountsLoadingMore, hasMoreAccounts, fetchAccountsByCategory, selectedCategory, accountPage]);

  const fetchProductData = async () => {
    setInitialLoading(true);

    try {
      const [packageRes, utilityRes, giftRes] = await Promise.all([
        getProductsByType("topup_package", 6),
        getProductsByType("utility_account", 6),
        getProductsByType("gift_card", 6),
      ]);

      setPackages(packageRes?.success ? packageRes.data.products || [] : []);
      setUtilities(utilityRes?.success ? utilityRes.data.products || [] : []);
      setGifts(giftRes?.success ? giftRes.data.products || [] : []);
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setInitialLoading(false);
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    appendDots: (dots) => (
      <div className="custom-dots-container">
        <ul> {dots} </ul>
      </div>
    ),
    customPaging: () => (
      <div className="custom-dot"></div> // Custom dot styling
    ),
    arrows: false,
  };

  if (initialLoading) {
    return (
      <div style={{ height: "100vh" }} className="loader-container dashboard-initial-loader">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <>
      <ToastNotification />
      <ScrollToTopButton />
      <section className="dashboard-banner-fullbleed">
        <div className="banner-slider dashboard-banner-card">
          <Slider {...settings}>
            {banners.map((banner, index) => (
              <div key={index}>
                {banner}
              </div>
            ))}
          </Slider>
        </div>
      </section>

      <div className="dashboard-container">
        <section className="dashboard-top-grid">
          <div className="dashboard-quick-category-shell">
            <div className="dashboard-quick-category-row">
              {categories.map((cat) => (
                <Link key={cat.link} to={cat.link} className="dashboard-quick-category-item">
                  <div className="dashboard-quick-category-icon-wrap">
                    <img src={cat.image} alt={cat.name} className="dashboard-quick-category-icon" />
                  </div>
                  <p className="dashboard-quick-category-name">{cat.name}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {accountsLoading && (
          <div className="dashboard-section-loader">
            <HashLoader size={22} color="var(--hash-loader-color)" />
          </div>
        )}

        <AccountSection
          title={"Tài Khoản Game"}
          accounts={accounts}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          navigate={navigate}
          hasMoreAccounts={hasMoreAccounts}
          accountsLoading={accountsLoading}
          accountsLoadingMore={accountsLoadingMore}
          onLoadMoreAccounts={handleLoadMoreAccounts}
        />
        <TopUpSection title={"Nạp Game Mobile"} packages={packages} />
        <UtilitySection title={"Tiện ích"} utilities={utilities} />
        <GiftCardSection title={"Gift Card"} utilities={gifts} />

        <div className="why-choose-us-container">
          <div className="why-choose-us-header">
            <h3 className="why-choose-us-title">Tại sao lại chọn chúng tôi?</h3>
            <p>Hệ thống được tối ưu cho tốc độ, độ ổn định và trải nghiệm mua hàng minh bạch.</p>
          </div>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="icon-title">
                <FaUser />
                <p>4.000+ người dùng</p>
              </div>
              <p>Được cộng đồng game thủ ngày càng đông đảo tại Việt Nam tin dùng.</p>
            </div>

            <div className="stat-item">
              <div className="icon-title">
                <MdElectricBolt />
                <p>Thời gian hoạt động 99,9%</p>
              </div>
              <p>Hiệu suất đáng tin cậy và ổn định mà bạn có thể tin tưởng, 24/7.</p>
            </div>

            <div className="stat-item">
              <div className="icon-title">
                <IoIosStar />
                <p>Đánh giá 4.9/5</p>
              </div>
              <p>Người dùng yêu thích chúng tôi! Sự hài lòng tuyệt đối trên mọi phương diện.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
